<template>
  <div class="fixed-app flex flex-col">
    <!-- ヘッダー -->
    <Header
      ref="header"
      :isShowPrevious="true"
      headerTitle="手配内容確認"
      :isShowClose="true"
      @clickPrevious="goToBackPage()"
      @clickClose="clickClose(true)"
    />
    <div class="w-full flex-1 relative">
      <!-- マップ -->
      <BaseMap
        ref="BaseMap"
        mapElementId="taxiReservationConfirmMap"
        :isTouchMap="false"
        :mapPaddingPx="46"
        :copyrightBottomSpace="18"
        @finish-generate-map="finishGenerateMap()"
      />
      <div
        class="bg-white absolute rounded-t-[10px] inset-x-0 bottom-[-1px] h-[14px] shadow z-10"
      ></div>
    </div>
    <div
      ref="confirmationCard"
      class="bg-white px-4 pb-6 pt-[7px]"
      :class="{'z-10': !isShowArrangeFareDetail}"
    >
      <div class="mb-4 text-W7 text-[15px] leading-[19px] text-left">
        この内容で手配します。よろしいですか？
      </div>
      <div class="w-full flex flex-col mb-4 border-y border-gray300 relative">
        <div class="divide-y border-gray300">
          <!-- 目安料金 -->
          <div class="flex h-[58px]">
            <!-- ラベル -->
            <div
              class="flex items-center justify-center bg-gray200 basis-[77px] text-W5 text-[13px] text-gray500"
            >
              目安料金
            </div>
            <!-- 金額表示 -->
            <div
              v-if="isSetEstimation"
              class="flex ml-3 items-center flex-auto"
              @click="isShowArrangeFareDetail = true"
            >
              <div class="flex flex-col text-justify">
                <!-- 金額/メータ料金/手数料説明 -->
                <div class="flex">
                  <div
                    v-if="hasAmount"
                    class="text-W7 text-[14px] leading-[21px]"
                  >
                    約
                  </div>
                  <!-- 金額/メータ料金 -->
                  <div class="text-W7" :class="estimatedAmountTextClass">
                    {{ estimatedAmountText }}
                  </div>
                  <div
                    v-if="hasAmount"
                    class="text-W7 text-[14px] leading-[21px] mr-1"
                  >
                    円
                  </div>
                  <div
                    v-if="hasAmount"
                    class="text-W4 text-[13px] leading-[21px]"
                  >
                    （手配料金を含む）
                  </div>
                  <!-- 手配料金ラベル -->
                  <div
                    v-else
                    class="text-W4 text-[13px] text-gray600 leading-[21px] ml-2"
                  >
                    + 手配料金
                  </div>
                </div>
                <!-- ピークチャージ・オフピーク割の表示 -->
                <span
                  v-if="hasAdaptationPeakChargeOrOffPeakDiscount"
                  class="text-W4 text-primary text-[11px]"
                >
                  {{
                    getMessagePeakChargeOrOffPeakDiscount(
                      estimatedResult.breakdown?.dynamicPricing?.type
                    )
                  }}
                </span>
              </div>
              <!-- 矢印アイコン -->
              <div v-if="isSetEstimation" class="absolute right-[10px]">
                <img class="h-3 w-3" src="@/assets/Icon_Right_Black.svg" />
              </div>
            </div>
            <div
              v-else
              class="w-[45%] text-right items-center content-center text-[14px] text-W4 text-gray400"
            >
              <span v-if="isEstimateSyncFailed">読込失敗</span>
            </div>
          </div>
          <!-- 移動時間 -->
          <div class="h-10 flex border-gray700">
            <div
              class="flex items-center justify-center bg-gray200 w-[77px] text-W5 text-[13px] text-gray500"
            >
              移動時間
            </div>
            <div v-if="isSetEstimation" class="ml-3 flex items-center">
              <div class="flex items-baseline">
                <span
                  v-if="hasDurationTime"
                  class="text-W7 text-[14px] leading-[21px]"
                >
                  約
                </span>
                <span v-if="isExistHour">
                  <span class="text-W7 text-[18px]">
                    {{ timeHour }}
                  </span>
                  <span class="text-W7 text-[14px] leading-[21px]">時間</span>
                </span>
                <span class="text-W7 text-[18px]">
                  {{ timeMinute }}
                </span>
                <span
                  v-if="hasDurationTime"
                  class="text-W7 text-[14px] leading-[21px]"
                >
                  分
                </span>
              </div>
            </div>
            <div
              v-else
              class="w-[45%] text-right items-center content-center text-[14px] text-W4 text-gray400"
            >
              <span v-if="isEstimateSyncFailed">読込失敗</span>
            </div>
          </div>
          <!-- 手配開始 -->
          <div class="h-[53px] flex border-gray700">
            <div
              class="flex items-center justify-center bg-gray200 w-[77px] text-W5 text-[13px] text-gray500"
            >
              手配開始
            </div>
            <div v-if="isSetEstimation" class="ml-3 py-4 flex items-center">
              <div class="flex-1">
                <div class="flex items-center leading-[21px]">
                  <span class="text-W7 text-[14px]">{{ arrangeNowText }}</span>
                  <div v-if="existArrivalTime" class="items-center">
                    <span
                      v-if="isSameEstimatedArrivalTime"
                      class="text-W4 text-[13px]"
                    >
                      （約{{ estimatedArrivalTime.minWaitTime }}分で乗車）
                    </span>
                    <span v-else class="text-W4 text-[13px]">
                      （約{{ estimatedArrivalTime.minWaitTime }}~{{
                        estimatedArrivalTime.maxWaitTime
                      }}分で乗車）
                    </span>
                  </div>
                </div>
                <div class="flex items-center text-[11px] leading-[17.6px]">
                  <span class="text-W4 text-gray400 mr-px">※</span>
                  <span class="text-W4 text-gray400">
                    時間指定の予約はできません
                  </span>
                </div>
              </div>
            </div>
            <div
              v-else
              class="w-[45%] text-right items-center content-center text-[14px] text-W4 text-gray400"
            >
              <span v-if="isEstimateSyncFailed">読込失敗</span>
            </div>
          </div>
        </div>
        <!-- ローディング -->
        <div
          v-if="!isSetEstimation && !isEstimateSyncFailed"
          class="absolute left-[50%] top-[25%]"
        >
          <Loading />
        </div>
      </div>
      <TheButton
        class="mb-2"
        text="お支払い方法の設定へすすむ"
        @click-button="onClickCallTaxi()"
      />
      <div class="text-W4 text-[11px] leading-[21px] text-center text-gray400">
        料金は降車時に確定します
      </div>
    </div>
    <!-- 手配料金説明パネル -->
    <div v-if="isShowArrangeFareDetail" id="overlay" class="overlay">
      <div class="overlay-layer">
        <BasePanel
          class="pointer-events-auto pb-[52px]"
          calcPanelHeightMode="panel"
          :isPanelIcon="false"
          :bottomSizeOfPanel="''"
          :roundSize="'8px'"
        >
          <!-- header要素からの相対座標でバナーを表示 -->
          <template v-slot:header>
            <Header
              ref="header"
              :isShowPrevious="true"
              headerTitle="目安料金の詳細"
              @clickPrevious="isShowArrangeFareDetail = false"
            />
          </template>
          <template v-slot:body>
            <div class="flex-col mt-4 mx-5 mb-4">
              <!-- 説明と料金一覧リンク -->
              <div class="text-left mb-4">
                <div class="text-W4 text-[13px] text-gray500">
                  地図上の経路や料金は実際とは異なる場合があります。
                </div>
                <div class="text-blue text-W5">
                  <div
                    class="w-[110px]"
                    @click="openGoTaxiAppArrangementFeeSite()"
                  >
                    料金一覧はこちら
                    <img
                      src="@/assets/Icon_Right_blue.svg"
                      class="inline h-[10px] w-[10px]"
                    />
                  </div>
                </div>
              </div>
              <!-- 料金表 -->
              <div class="divide-y border-y border-gray300">
                <!-- 運賃 -->
                <div class="h-14 flex">
                  <!-- ヘッダー -->
                  <div
                    class="flex items-center pl-3 bg-gray200 w-[134px] text-W4 text-[13px] text-gray500"
                  >
                    運賃
                  </div>
                  <!-- 値 -->
                  <div
                    class="w-[201px] text-right items-center content-center text-[14px] ml-auto"
                  >
                    <div class="flex flex-col justify-items-end pr-3">
                      <div
                        class="flex ml-auto text-W4 text-[14px] leading-[21px]"
                      >
                        <!-- 金額/メータ料金-->
                        <div v-if="hasAmount">約</div>
                        <!-- 金額/メータ料金 -->
                        <div>
                          {{ estimatedTaxiFareText }}
                        </div>
                        <div v-if="hasAmount">円</div>
                      </div>
                      <div class="text-[11px] text-gray400">
                        降車時に確定します
                      </div>
                    </div>
                  </div>
                </div>
                <!-- 迎車料金 -->
                <div class="h-10 flex border-gray700">
                  <!-- ヘッダー -->
                  <div
                    class="flex items-center pl-3 bg-gray200 w-[134px] text-W5 text-[13px] text-gray500"
                  >
                    迎車料金
                    <sup>※1</sup>
                  </div>
                  <!-- 値 -->
                  <div
                    class="w-[201px] text-right items-center content-center text-[14px] ml-auto"
                  >
                    <span class="pr-3">
                      {{ displayFee(estimatedResult.breakdown?.pickupFee) }}
                    </span>
                  </div>
                </div>
                <!-- アプリ手配料 -->
                <div class="h-10 flex border-gray700">
                  <!-- ヘッダー -->
                  <div
                    class="flex items-center pl-3 bg-gray200 w-[134px] text-W5 text-[13px] text-gray500"
                  >
                    アプリ手配料
                    <sup>※2</sup>
                  </div>
                  <!-- 値 -->
                  <div
                    class="w-[201px] text-right items-center content-center text-[14px] ml-auto"
                  >
                    <span class="pr-3">
                      {{
                        displayFee(estimatedResult.breakdown?.arrangementFee)
                      }}
                    </span>
                  </div>
                </div>
                <!-- オフピーク割 -->
                <div
                  class="flex border-gray700"
                  :class="heightDetailPricingType(isOffPeakDiscount)"
                >
                  <!-- ヘッダー -->
                  <div
                    class="flex items-center pl-3 bg-gray200 w-[134px] text-W5 text-[13px] text-gray500"
                  >
                    オフピーク割
                  </div>
                  <!-- 値 -->
                  <div
                    class="w-[201px] text-right items-center content-center text-[14px] ml-auto"
                  >
                    <div
                      v-if="isOffPeakDiscount"
                      class="flex flex-col justify-items-end pr-3"
                    >
                      <span
                        v-if="hasDynamicPricingAmountValue"
                        class="text-[14px]"
                      >
                        約{{ dynamicPricingAmount }}円引
                      </span>
                      <span v-else class="text-W4 text-[14px]">適用</span>
                      <span class="text-[11px] text-gray400">
                        運賃+迎車料金の{{ dynamicPricing.rate }}%分
                      </span>
                      <span class="text-[11px] text-gray400">
                        （最大{{ minAmount }}円引）
                      </span>
                    </div>
                    <div v-else class="flex flex-col justify-items-end pr-3">
                      -
                    </div>
                  </div>
                </div>
                <!-- ピークチャージ -->
                <div
                  class="flex border-gray700"
                  :class="heightDetailPricingType(isPeakCharge)"
                >
                  <!-- ヘッダー -->
                  <div
                    class="flex items-center pl-3 bg-gray200 w-[134px] text-W5 text-[13px] text-gray500"
                  >
                    ピークチャージ
                  </div>
                  <!-- 値 -->
                  <div
                    class="w-[201px] text-right items-center content-center text-[14px] ml-auto"
                  >
                    <div
                      v-if="isPeakCharge"
                      class="flex flex-col justify-items-end pr-3"
                    >
                      <span
                        v-if="hasDynamicPricingAmountValue"
                        class="text-[14px]"
                      >
                        約{{ dynamicPricingAmount }}円
                      </span>
                      <span v-else class="text-W4 text-[14px]">適用</span>
                      <span class="text-[11px] text-gray400">
                        運賃+迎車料金の{{ dynamicPricing.rate }}%分
                      </span>
                      <span class="text-[11px] text-gray400">
                        （最大{{ maxAmount }}円）
                      </span>
                    </div>
                    <div v-else class="flex flex-col justify-items-end pr-3">
                      -
                    </div>
                  </div>
                </div>
              </div>
              <!-- 注釈リスト -->
              <div class="text-gray500 text-W4 text-[13px] leading-[18px] mt-4">
                <!-- ※1 -->
                <div class="flex text-left">
                  <div class="mr-2">※1</div>
                  <div>タクシー会社規定の迎車料金がかかります。</div>
                </div>
                <!-- ※2 -->
                <div class="flex text-left">
                  <div class="mr-2">※2</div>
                  <div>タクシー会社に応じてアプリ手配料がかかります。</div>
                </div>
              </div>
            </div>
          </template>
        </BasePanel>
      </div>
    </div>
  </div>
</template>
<script>
import {clickClose} from '@/utils/arrangementUtil'
import Loading from '@/components/Loading.vue'
import Header from '@/components/organisms/Header.vue'
import TheButton from '@/components/atoms/TheButton.vue'
import BaseMap from '@/components/atoms/BaseMap.vue'
import BasePanel from '@/components/atoms/BasePanel.vue'
import Util from '@/mixins/util'
import {navigateTo} from '@/router'
import {constant} from '@/const/const'
import log from 'loglevel'
const EMPTY_NUM = 0
const MINUTE_OF_HOUR = 60
const SECOND_OF_HOUR = 3600

const {PEAK_CHARGE, OFF_PEAK_DISCOUNT} = constant.TAXI_APPLICABLE_AMOUNT

const PICKUP_ICON_SIZE = {
  height: 46,
  width: 36,
}
const DROP_ICON_SIZE = {
  height: 44,
  width: 44,
}

const TaxiReservationConfirm = {
  name: 'TaxiReservationConfirm',
  components: {Loading, Header, TheButton, BaseMap, BasePanel},
  mixins: [Util],
  data() {
    return {
      estimatedResult: {}, // 見積もり結果 空オブジェクト:読込中 null:読込失敗
      glMarkers: [], // 乗車地・目的地用のアイコンリスト
      isShowArrangeFareDetail: false,
      estimatedArrivalTime:
        this.$store.state.MobilityReservationStore.taxi.estimatedArrival, // 到着予想時間
    }
  },
  computed: {
    /**
     * ルート情報を返す
     */
    routeInfo() {
      return this.$store.state.MobilityReservationStore.taxi.routeInfo
    },
    /**
     * 乗車地の住所
     */
    pickUp() {
      return this.routeInfo.pickUp
    },
    /**
     * 目的地の住所
     */
    dropOff() {
      return this.routeInfo.dropOff
    },
    /**
     * 見積もり情報がAPIから連携されていない
     */
    isEstimateSyncFailed() {
      return this.isNull(this.estimatedResult)
    },
    /**
     * 見積もりがあるかを判定
     */
    isSetEstimation() {
      return !this.isEstimateSyncFailed && !this.isEmpty(this.estimatedResult)
    },
    /**
     * 金額があること
     */
    hasAmount() {
      return !this.isNull(this.estimatedResult?.amount)
    },
    /**
     * 表示する見積もり金額テキスト
     */
    estimatedAmountText() {
      if (this.isEstimateSyncFailed) return '読込失敗'
      return this.isSetEstimation && this.hasAmount
        ? this.addCommaForInteger(this.estimatedResult.amount)
        : 'メーター料金'
    },
    /**
     * 表示する運賃金額テキスト
     */
    estimatedTaxiFareText() {
      return this.isSetEstimation && this.hasAmount
        ? this.addCommaForInteger(this.estimatedResult.breakdown.fare)
        : 'メーター料金'
    },
    /**
     * 表示する見積もり金額テキストに付与するクラス
     */
    estimatedAmountTextClass() {
      return this.isSetEstimation && this.hasAmount
        ? 'text-[18px]'
        : 'text-[14px]'
    },
    /**
     * 乗車時間があること
     */
    hasDurationTime() {
      return (
        typeof this.estimatedResult.duration === 'number' &&
        this.estimatedResult.duration > 0 // eslint-disable-line no-magic-numbers
      )
    },
    /**
     * 総時間(秒)から時間(h)を算出
     */
    timeHour() {
      return this.isSetEstimation
        ? Math.floor(this.estimatedResult.duration / SECOND_OF_HOUR)
        : EMPTY_NUM
    },
    /**
     * 総時間(秒)から分(mm)を算出
     */
    timeMinute() {
      if (!this.hasDurationTime) return '-'
      return Math.floor(
        (this.estimatedResult.duration % SECOND_OF_HOUR) / MINUTE_OF_HOUR
      )
    },
    /**
     * 総時間(秒)から算出した時間(h)が存在するか
     */
    isExistHour() {
      return EMPTY_NUM < this.timeHour
    },
    /**
     * 目的地が指定されているかどうか
     */
    isSettingDropOff() {
      return this.dropOff.lat != '' && this.dropOff.lon != ''
    },
    /**
     * 到着予想時間が同じであるかどうか
     */
    isSameEstimatedArrivalTime() {
      return (
        this.estimatedArrivalTime.minWaitTime ===
        this.estimatedArrivalTime.maxWaitTime
      )
    },
    /**
     * 迎車見込み時間が存在するかどうか
     */
    existArrivalTime() {
      return (
        this.estimatedArrivalTime.maxWaitTime !== null &&
        this.estimatedArrivalTime.minWaitTime !== null
      )
    },
    /**
     * ピークチャージ対象であること
     */
    isPeakCharge() {
      return (
        this.estimatedResult?.breakdown?.dynamicPricing?.type === PEAK_CHARGE
      )
    },
    /**
     * オフピーク割であること
     */
    isOffPeakDiscount() {
      return (
        this.estimatedResult?.breakdown?.dynamicPricing?.type ===
        OFF_PEAK_DISCOUNT
      )
    },
    arrangeNowText() {
      if (this.isEstimateSyncFailed) return '読込失敗'
      return '今すぐ'
    },
    /**
     * ピークチャージ・オフピーク割があること
     */
    hasAdaptationPeakChargeOrOffPeakDiscount() {
      return this.isPeakCharge || this.isOffPeakDiscount
    },
    /**
     * オフピーク割/ピークチャージの情報
     */
    dynamicPricing() {
      return this.hasAdaptationPeakChargeOrOffPeakDiscount
        ? this.estimatedResult.breakdown?.dynamicPricing
        : {}
    },
    /**
     * オフピーク割/ピークチャージの適用金額があること
     */
    hasDynamicPricingAmountValue() {
      const amount = this.estimatedResult.breakdown?.dynamicPricing.amount
      return typeof amount === 'number' && Number.isFinite(amount) && 0 < amount // eslint-disable-line no-magic-numbers
    },
    /**
     * 適用金額
     */
    dynamicPricingAmount() {
      return this.addCommaForInteger(this.dynamicPricing.amount)
    },
    /**
     * 最大適用料金
     */
    maxAmount() {
      return this.addCommaForInteger(Math.abs(this.dynamicPricing.maxAmount))
    },
    /**
     * 最小適用料金
     */
    minAmount() {
      return this.addCommaForInteger(Math.abs(this.dynamicPricing.minAmount))
    },
  },
  created() {
    this.setUp()
  },
  methods: {
    /** 見積もり情報の準備 */
    setUp() {
      // 支払確認画面から戻ってきた場合API実行させない
      this.estimatedResult =
        this.$store.state.MobilityReservationStore.taxi.estimatedResult
      if (
        this.isNull(this.estimatedResult) ||
        this.isEmpty(this.estimatedResult)
      ) {
        // API実行
        this.getTaxiEstimations()
      }
    },
    /**
     * 詳細モーダル用のピークチャージ文言を表示する際に必要な高さを設定
     * @param {boolean} hasType ピークチャージもしくはオフピーク割である
     * @returns {string} セルの高さ用のtailwindクラス
     */
    heightDetailPricingType(hasType) {
      return hasType ? 'h-[73px]' : 'h-10'
    },
    /**
     * ピークチャージかオフピーク割を表示
     * @param {string|undefined} pricingType ピークチャージもしくはオフピーク割
     * @returns {string} 適用対象地域であれば、表示用文言
     */
    getMessagePeakChargeOrOffPeakDiscount(pricingType) {
      switch (pricingType) {
        case PEAK_CHARGE:
          return '（ピークチャージ適用中）'
        case OFF_PEAK_DISCOUNT:
          return '（オフピーク割適用中）'
        default:
          return ''
      }
    },
    /**
     * 料金の表示
     * @param {number|undefined} target 料金
     * @returns {string} 表示用文言
     */
    displayFee(target) {
      const fee = target
      return typeof fee === 'number'
        ? `約${this.addCommaForInteger(fee)}円`
        : '-'
    },
    /**
     * ×ボタン押下処理
     * @param {Boolean} isInitialize 初期化を行うか
     */
    clickClose(isInitialize) {
      clickClose(isInitialize)
    },
    /**
     * マップ生成後の処理
     */
    finishGenerateMap() {
      // 乗車地と目的地にアイコンを配置
      this.setPickupAndDropOffIcon()
      // 乗車地と目的地が収まるようマップを移動
      this.fitPickupAndDropOffSpot()
    },
    /**
     * 乗車地及び目的地のアイコンをマップに配置する
     */
    setPickupAndDropOffIcon() {
      let iconList = []
      // 乗車地用のアイコン情報を生成&追加
      const pickUpIcon = {
        name: 'pick up',
        lat: Number(this.pickUp.lat),
        lon: Number(this.pickUp.lon),
        size: PICKUP_ICON_SIZE,
        icon: 'arrangementIcon/taxi/IconPickUpPin.svg',
      }
      iconList.push(pickUpIcon)

      // 目的地の指定がある場合のみ、目的地用のアイコン情報を生成&追加
      if (this.isSettingDropOff) {
        const dropOffIcon = {
          name: 'drop off',
          lat: Number(this.dropOff.lat),
          lon: Number(this.dropOff.lon),
          size: DROP_ICON_SIZE,
          icon: 'Icon_Map_Place.svg',
        }
        iconList.push(dropOffIcon)
      }

      // 作成したアイコン情報からGLMarkerを生成し、描画
      this.glMarkers = this.$refs.BaseMap.generateGlMarkers(iconList)
      this.$refs.BaseMap.addGLMarkers(this.glMarkers)
    },
    /**
     * 乗車地と目的地に合わせて画面を移動
     */
    fitPickupAndDropOffSpot() {
      const pickUpSpot = {
        lat: Number(this.pickUp.lat),
        lon: Number(this.pickUp.lon),
      }

      if (this.isSettingDropOff) {
        // 目的地の指定があれば、乗車地と目的地が収まる縮尺・中心地にマップを移動
        const dropOffSpot = {
          lat: Number(this.dropOff.lat),
          lon: Number(this.dropOff.lon),
        }
        const spotList = [pickUpSpot, dropOffSpot]
        this.$refs.BaseMap.fitMultipleLatLon(spotList)
      } else {
        // 目的地の指定がない場合、乗車地を中心・固定縮尺でマップ移動
        this.$refs.BaseMap.callFitWithLatLon(pickUpSpot.lat, pickUpSpot.lon)
      }
    },
    /**
     * 戻るボタン押下処理
     */
    goToBackPage() {
      // 到着予想時間を初期化
      this.$store.commit('MobilityReservationStore/initEstimatedArrivalTime')
      // 見積もりUUIDの初期化
      this.$store.commit('MobilityReservationStore/initEstimationResult')
      navigateTo({name: this.$config.DISPLAY_TAXI_RESERVATION_TOP})
    },
    /**
     * タクシー見積もり処理
     */
    getTaxiEstimations() {
      // 目的地指定がない場合も、そのままAPIを実行させる
      const dropOffToApi = this.isSettingDropOff ? this.dropOff : undefined
      const vm = this

      // 成功時はメモリに結果を格納
      const success = (result) => {
        this.estimatedResult = result
        // APIの結果を保存
        this.$store.commit(
          'MobilityReservationStore/updateEstimationResult',
          result
        )
      }
      const failed = () => {
        this.estimatedResult = null // 読込失敗にする
      }
      const error = (e) => {
        log.info(e)
        this.estimatedResult = null // 読込失敗にする
      }

      // タクシー予約見積もりAPI実行
      vm.$store.dispatch('MobilityReservationStore/getTaxiEstimations', {
        success: success,
        failed: failed,
        error: error,
        pickUp: this.pickUp,
        dropOff: dropOffToApi,
      })
    },
    /**
     * クリックイベントの伝搬を停止
     */
    stopEvent(e) {
      e.stopPropagation()
    },
    /**
     * GoTaxiのアプリ手配料一覧ページにジャンプ
     */
    openGoTaxiAppArrangementFeeSite() {
      window.open(
        process.env.VUE_APP_GO_TAXI_APP_ARRANGEMENT_FEE_URL,
        '_blank',
        'noreferrer'
      )
    },
    /**
     * お支払い方法の設定へすすむボタン押下処理
     */
    onClickCallTaxi() {
      navigateTo({
        name: this.$config.DISPLAY_TAXI_RESERVATION_PAYMENT_CONFIRM,
      })
    },
  },
}
export default TaxiReservationConfirm
</script>
<style scoped>
.overlay {
  position: fixed;
  z-index: 5000;
  display: table;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  -webkit-transform: translate3d(0, 0, 5000px);
  transform: translate3d(0, 0, 5000px);
}
.overlay-layer {
  display: flex;
  align-items: flex-end;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #1a1c2133;
  backdrop-filter: blur(3px) brightness(81%);
}
.translateZ-6000 {
  transform: translateZ(6000px);
}
.translateZ-7000 {
  z-index: 7000;
  transform: translateZ(7000px);
}
.shadow {
  filter: drop-shadow(0 -3px 8px #00000029);
}
</style>
